import { useEffect, useState } from "react";
import { Avatar, Btn, DataTable, Icon, Pill, T, type TableColumn } from "@v0y4ge/jrni-ui";
import { useNav } from "../../shell/router";
import { can } from "../../shell/access";
import { useT } from "../../lib/i18n";
import { RowActions } from "../../shell/composites";
import { tabExport } from "../../shell/list-composites";
import { eventActions } from "./eventActions";
import type { UIEvent } from "../../lib/events-adapter";

/** Payments (2026-08-04) — minor-units → display money for the Revenue column
 * and the Paid pill. Symbol from the row currency; defaults to £. Presentational
 * only (the UI never computes an amount — it formats what the adapter carries). */
function fmtMoney(minor?: number, currency?: string): string {
  if (minor == null) return "—";
  const sym = currency === "USD" ? "$" : currency === "EUR" ? "€" : "£";
  return `${sym}${(minor / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

/**
 * EventsInlineTable — the Events page table. Migrated (2026-08-06) from a
 * hand-rolled `<table>` + hand-rolled bulk bar to the jrni-ui `DataTable`
 * (typed columns, header sort, row selection, selection toolbar). The cell
 * content — hero tile, Paid/Free pill, capacity bar, host Avatar, status Pill,
 * RowActions — is preserved verbatim; only the table chrome is now a common
 * component. Sort is driven by the parent (EventsList owns the toggle); the
 * DataTable reports which column header was clicked.
 *
 * Approvals column gated off until the approvals data layer ports (flag);
 * bulk Export builds a real CSV of the selected rows (tabExport); bulk Archive
 * is honest-disabled — no events-archive op exists at 93196e63 (REQ-084).
 */
export function EventsInlineTable({ events, sortKey, sortDir, onSort }: {
  events: UIEvent[];
  sortKey: string;
  sortDir: "asc" | "desc";
  onSort?: (sortId: string) => void;
}) {
  const nav = useNav();
  const t = useT();
  const approvalsOn = false; // TODO: wire ApprovalsStore when the approvals layer ports
  const paymentsOn = events.some((e) => e.isPaid != null || e.revenueMinor != null);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const visibleIds = events.map((e) => e.id);
  useEffect(() => {
    setSelected((prev) => {
      const next = new Set([...prev].filter((id) => visibleIds.includes(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [visibleIds.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  // Bulk Export does real work — CSV of the selected rows via tabExport
  // (download-then-toast). No events-archive op exists → Archive honest-disabled.
  const bulkExport = () => {
    const rows = events.filter((e) => selected.has(e.id)).map((e) => ({ title: e.title, status: e.status, date: e.date, time: e.time, location: e.location, registered: e.registered, capacity: e.capacity, revenue: e.revenueMinor != null ? fmtMoney(e.revenueMinor, e.currency) : "" }));
    tabExport("events", rows);
  };

  const columns: TableColumn<UIEvent>[] = [
    {
      id: "title", header: t("events.inlineTable.headers.event", "Event"), sortable: true, minWidth: 240,
      cell: (ev) => (
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <span aria-hidden="true" style={{ width: 36, height: 36, borderRadius: 6, flexShrink: 0, background: ev.heroImage ? `url(${ev.heroImage}) center/cover` : ev.hue, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 13 }}>{ev.heroImage ? null : ev.title.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}</span>
          <div style={{ minWidth: 0 }}>
            <div style={{ color: T.n1, fontWeight: 600 }}>{ev.title}</div>
            <div style={{ color: T.soft, fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
              <span>{ev.template}</span>
              {ev.isPaid === true ? <Pill tone="primary">{ev.priceFromMinor != null ? `${t("events.inlineTable.paid", "Paid")} · ${fmtMoney(ev.priceFromMinor, ev.currency)}` : t("events.inlineTable.paid", "Paid")}</Pill> :
                ev.isPaid === false ? <Pill tone="neutral">{t("events.inlineTable.free", "Free")}</Pill> : null}
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "when", header: t("events.inlineTable.headers.when", "When"), sortable: true,
      cell: (ev) => (<div><div style={{ color: T.n1 }}>{ev.date}</div><div style={{ color: T.soft, fontSize: 12 }}>{ev.time}</div></div>),
    },
    { id: "location", header: t("events.inlineTable.headers.location", "Location"), sortable: true, cell: (ev) => ev.location },
    {
      id: "host", header: t("events.inlineTable.headers.host", "Host"), sortable: true,
      cell: (ev) => (<div style={{ display: "flex", alignItems: "center", gap: 8 }}><Avatar size={24} initials={ev.host.split(" ").map((w) => w[0]).join("")} color={ev.hue} />{ev.host}</div>),
    },
    {
      id: "registered", header: t("events.inlineTable.headers.registered", "Registered"), sortable: true,
      cell: (ev) => (
        <div>
          <div style={{ color: T.n1, fontWeight: 600 }}>{ev.registered}<span style={{ color: T.soft, fontWeight: 400 }}> / {ev.capacity > 0 ? ev.capacity : "∞"}</span></div>
          <div style={{ height: 4, background: T.n5, borderRadius: 999, overflow: "hidden", marginTop: 4, maxWidth: 100 }}>
            <div style={{ height: "100%", width: `${ev.capacity > 0 ? Math.min(100, (ev.registered / ev.capacity) * 100) : 0}%`, background: ev.status === "live" ? T.green1 : T.p1 }} />
          </div>
        </div>
      ),
    },
    ...(paymentsOn ? [{
      id: "revenue", header: t("events.inlineTable.headers.revenue", "Revenue"), align: "right" as const,
      cell: (ev: UIEvent) => (ev.isPaid ? <span style={{ color: T.n1, fontWeight: 600 }}>{fmtMoney(ev.revenueMinor, ev.currency)}</span> : <span style={{ color: T.soft }}>—</span>),
    }] : []),
    {
      id: "status", header: t("events.inlineTable.headers.status", "Status"), sortable: true,
      cell: (ev) => (
        ev.status === "live" ? <Pill tone="success" dot>{t("events.status.live", "Live")}</Pill> :
          ev.status === "canceled" ? <Pill tone="danger">{t("events.status.cancelled", "Cancelled")}</Pill> :
          ev.status === "draft" ? <Pill>{t("events.status.draft", "Draft")}</Pill> :
          ev.status === "past" ? <Pill tone="neutral">{t("events.status.ended", "Ended")}</Pill> :
          <Pill tone="primary">{t("events.status.upcoming", "Upcoming")}</Pill>
      ),
    },
    {
      id: "actions", header: t("events.inlineTable.headers.actions", "Actions"), align: "right" as const, minWidth: 160,
      cell: (ev) => (
        <div onClick={(e) => e.stopPropagation()}>
          <RowActions
            onView={() => nav.go("event.public", { eventId: ev.id })}
            viewLabel={t("events.inlineTable.actions.preview", "Preview")}
            onEdit={() => eventActions.editEvent(ev, nav)}
            editLabel={t("events.inlineTable.actions.editDetails", "Edit details")}
            onDuplicate={() => eventActions.duplicateEvent(ev)}
            audit={{ type: "event", id: ev.id, name: ev.title }}
            moreHeader={ev.title}
            moreItems={[
              { icon: <Icon.settings size={14} />, label: t("events.inlineTable.actions.manageEvent", "Manage event"), sub: t("events.inlineTable.actions.registrationsCheckInDetails", "Registrations, check-in & details"), onClick: () => nav.openEvent(ev) },
              { icon: <Icon.external size={14} />, label: t("events.inlineTable.actions.openPublicPage", "Open public page"), onClick: () => eventActions.openLandingPage(ev, nav) },
              { icon: <Icon.link size={14} />, label: t("events.inlineTable.actions.copyPublicUrl", "Copy public URL"), onClick: () => eventActions.copyPublicUrl(ev) },
              "divider",
              { icon: <Icon.layers size={14} />, label: t("events.inlineTable.actions.saveAsTemplate", "Save as template"), onClick: () => eventActions.saveAsTemplate(ev, nav) },
              { icon: <Icon.download size={14} />, label: t("events.inlineTable.actions.exportGuestsCsv", "Export guests (CSV)"), onClick: () => eventActions.exportGuestList(ev) },
              "divider",
              ev.status === "draft"
                ? { icon: <Icon.trash size={14} />, label: t("events.inlineTable.actions.deleteDraft", "Delete draft"), danger: true, onClick: () => eventActions.cancelOrDiscard(ev, nav) }
                : { icon: <Icon.x size={14} />, label: t("events.inlineTable.actions.cancelEvent", "Cancel event"), danger: true, sub: t("events.inlineTable.actions.notifiesRegisteredGuests", "Notifies registered guests"), onClick: () => eventActions.cancelOrDiscard(ev, nav) },
            ]}
          />
        </div>
      ),
    },
  ];

  return (
    <DataTable<UIEvent>
      columns={columns}
      rows={events}
      getRowId={(e) => e.id}
      selectedIds={selected}
      onRowSelect={(id, sel) => setSelected((s) => { const n = new Set(s); if (sel) n.add(id); else n.delete(id); return n; })}
      onSelectAll={(sel) => setSelected(sel ? new Set(events.map((e) => e.id)) : new Set())}
      sort={{ columnId: sortKey, direction: sortDir }}
      onSortChange={(next) => onSort?.(next.columnId)}
      onRowClick={(ev) => nav.openEvent(ev)}
      selectionNoun={t("events.inlineTable.selected", "selected")}
      bulkActions={<>
        {can("reports.export") ? <Btn variant="ghost" size="sm" icon={<Icon.csv size={12} />} onClick={bulkExport}>{t("events.inlineTable.bulk.export", "Export")}</Btn> : null}
        {can("events.write") ? <Btn variant="ghost" size="sm" icon={<Icon.archive size={12} />} disabled title={t("events.inlineTable.bulk.archiveUnavailable", "Not available yet — no events-archive operation is declared in the wiring kit (REQ-084).")}>{t("events.inlineTable.bulk.archive", "Archive")}</Btn> : null}
      </>}
    />
  );
}
